import { useState } from 'react'
import { Toggle, Button, Chip, PriorityChip } from '@/components/ui'
import type { Prioridad } from '@/mock'
import { Save, Plus, Users, Clock, AlertTriangle, Scale } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Regla {
  id: string
  nombre: string
  descripcion: string
  prioridad: Prioridad
  on: boolean
}

const reglasInit: Regla[] = [
  { id: 'r1', nombre: 'Palabras clave de amenaza', descripcion: 'denuncia, demanda, abogado, público, redes sociales', prioridad: 'P1', on: true },
  { id: 'r2', nombre: 'Pide hablar con asesor', descripcion: 'asesor, operador, humano, persona real, agente', prioridad: 'P2', on: true },
  { id: 'r3', nombre: 'Plan de pago no estándar', descripcion: 'Cuando pide cuotas custom o montos distintos a los definidos', prioridad: 'P2', on: true },
  { id: 'r4', nombre: 'IA sin avance en 3 intentos', descripcion: 'Si el copilot respondió 3 veces y el cliente no quedó satisfecho', prioridad: 'P2', on: true },
  { id: 'r5', nombre: 'Comprobante rechazado 2+ veces', descripcion: 'Cliente insiste con comprobantes que no validan', prioridad: 'P1', on: true },
  { id: 'r6', nombre: 'Tono agresivo detectado (IA)', descripcion: 'Sentiment analysis negativo + insultos', prioridad: 'P1', on: true },
  { id: 'r7', nombre: 'Cliente VIP automático', descripcion: 'Clientes del segmento premium siempre a humano', prioridad: 'P3', on: false },
]

type ModoAsignacion = 'equilibrada' | 'round-robin' | 'manual'

const modos: { k: ModoAsignacion; l: string; d: string }[] = [
  { k: 'equilibrada', l: 'Carga equilibrada', d: 'Asigna al agente con menos chats activos' },
  { k: 'round-robin', l: 'Round-robin', d: 'Rota entre todos los agentes en orden' },
  { k: 'manual', l: 'Manual', d: 'Supervisor asigna uno por uno' },
]

export function Reglas() {
  const [reglas, setReglas] = useState(reglasInit)
  const [modo, setModo] = useState<ModoAsignacion>('equilibrada')
  const [maxChats, setMaxChats] = useState(5)
  const [sla1, setSla1] = useState(3)
  const [sla2, setSla2] = useState(10)
  const [sla3, setSla3] = useState(30)

  return (
    <div className="space-y-5">
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-500" />
            <h2 className="font-display text-lg text-navy-700">Triggers automáticos de escalación</h2>
          </div>
          <Button variant="outline" size="sm"><Plus className="w-3.5 h-3.5" /> Agregar regla</Button>
        </header>
        <div className="divide-y divide-slate-100">
          {reglas.map(r => (
            <div key={r.id} className={cn('py-3 flex items-start gap-4', !r.on && 'opacity-50')}>
              <PriorityChip prioridad={r.prioridad} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-navy-700 text-sm">{r.nombre}</div>
                <div className="text-xs text-slate-500 mt-0.5 font-mono">{r.descripcion}</div>
              </div>
              <Toggle on={r.on} onChange={v => setReglas(prev => prev.map(x => x.id === r.id ? { ...x, on: v } : x))} />
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <header className="flex items-center gap-2 mb-4">
          <Scale className="w-5 h-5 text-blue-500" />
          <h2 className="font-display text-lg text-navy-700">Modo de asignación</h2>
        </header>
        <div className="grid grid-cols-3 gap-3 mb-5">
          {modos.map(opt => (
            <button
              key={opt.k}
              onClick={() => setModo(opt.k)}
              className={cn(
                'p-4 rounded-xl border-2 text-left transition',
                modo === opt.k ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
              )}
            >
              <div className="font-semibold text-navy-700">{opt.l}</div>
              <div className="text-xs text-slate-500 mt-1">{opt.d}</div>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 bg-slate-50 rounded-lg px-4 py-3">
          <Users className="w-4 h-4 text-slate-400" />
          <label className="text-sm text-slate-700 flex-1">Máximo chats por agente a la vez</label>
          <input
            type="number"
            value={maxChats}
            onChange={e => setMaxChats(Number(e.target.value))}
            min={1}
            max={20}
            className="w-20 px-3 py-1.5 border border-slate-200 rounded-md text-sm font-mono bg-white text-center"
          />
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <header className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-amber-500" />
          <h2 className="font-display text-lg text-navy-700">Horarios y SLAs</h2>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">Horarios de atención humana</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2">
                <span className="text-slate-700">Lun - Vie</span>
                <span className="font-mono font-semibold text-navy-700">08:00 – 18:00</span>
              </div>
              <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2">
                <span className="text-slate-700">Sábados</span>
                <span className="font-mono font-semibold text-navy-700">09:00 – 13:00</span>
              </div>
              <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2 opacity-50">
                <span className="text-slate-700">Domingos</span>
                <span className="font-mono text-slate-400">Cerrado</span>
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">SLAs por prioridad</h3>
            <div className="space-y-2 text-sm">
              <SlaRow p="P1" label="Urgente" value={sla1} onChange={setSla1} />
              <SlaRow p="P2" label="Alta" value={sla2} onChange={setSla2} />
              <SlaRow p="P3" label="Normal" value={sla3} onChange={setSla3} />
            </div>
          </div>
        </div>
      </section>

      <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
        <div>
          <Chip variant="emerald">Última edición hace 2d por hernan</Chip>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline">Descartar cambios</Button>
          <Button variant="amber"><Save className="w-4 h-4" /> Guardar reglas</Button>
        </div>
      </div>
    </div>
  )
}

function SlaRow({ p, label, value, onChange }: { p: Prioridad; label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2 gap-3">
      <div className="flex items-center gap-2">
        <PriorityChip prioridad={p} />
        <span className="text-slate-700">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">menos de</span>
        <input
          type="number"
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          className="w-16 px-2 py-1 border border-slate-200 rounded-md text-sm font-mono bg-white text-center"
        />
        <span className="text-sm text-slate-700 font-mono">min</span>
      </div>
    </div>
  )
}
